import type { 
  AIClient, 
  AIAnalysisRequest, 
  AIAnalysisResult, 
  FallbackConfig, 
  FallbackAttempt
} from '$lib/types/ai';

/**
 * Abstract base class for streaming AI clients with fallback mechanisms
 * Provides common functionality for exponential backoff, timeout handling, and fallback chains
 */
export abstract class StreamingAIClient implements AIClient {
  private fallbackHistory: FallbackAttempt[] = [];
  
  constructor(
    protected config: FallbackConfig,
    protected currentSettings: any // AISettings from the current level
  ) {}

  // Abstract methods to be implemented by concrete streaming clients
  abstract analyzeCorrections(request: AIAnalysisRequest): Promise<AIAnalysisResult[]>;
  abstract isAvailable(): boolean | Promise<boolean>;
  abstract getModelName(): string;
  abstract sendMessage(prompt: string): Promise<string>;
  
  /**
   * Check if this client supports streaming
   */
  supportsStreaming(): boolean {
    return true;
  }

  /**
   * Main streaming analysis method with fallback chain
   */
  async analyzeCorrectionsStreaming(
    request: AIAnalysisRequest,
    onProgress?: (chunk: string) => void,
    onComplete?: (results: AIAnalysisResult[]) => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    const startTime = Date.now();
    
    try {
      // Try primary provider first
      await this.attemptStreamingAnalysis(
        this.config.primary, 
        'primary',
        request, 
        onProgress, 
        onComplete,
        1
      );
    } catch (primaryError) {
      // Record primary failure
      this.recordFallbackAttempt('primary', primaryError as Error, startTime);
      
      if (this.config.secondary) {
        try {
          // Try secondary provider
          await this.attemptStreamingAnalysis(
            this.config.secondary,
            'secondary', 
            request, 
            onProgress, 
            onComplete,
            1
          );
          return;
        } catch (secondaryError) {
          // Record secondary failure
          this.recordFallbackAttempt('secondary', secondaryError as Error, startTime);
        }
      }
      
      if (this.config.tertiary) {
        try {
          // Try tertiary provider
          await this.attemptStreamingAnalysis(
            this.config.tertiary,
            'tertiary', 
            request, 
            onProgress, 
            onComplete,
            1
          );
          return;
        } catch (tertiaryError) {
          // Record tertiary failure
          this.recordFallbackAttempt('tertiary', tertiaryError as Error, startTime);
        }
      }
      
      // All providers failed - call error handler
      const finalError = new Error(
        `All AI providers failed. Primary: ${(primaryError as Error).message}`
      );
      onError?.(finalError);
      throw finalError;
    }
  }

  /**
   * Attempt streaming analysis with a specific provider and retry logic
   */
  private async attemptStreamingAnalysis(
    settings: any,
    level: 'primary' | 'secondary' | 'tertiary',
    request: AIAnalysisRequest,
    onProgress?: (chunk: string) => void,
    onComplete?: (results: AIAnalysisResult[]) => void,
    attempt: number = 1
  ): Promise<void> {
    const maxRetries = this.config.maxRetries;
    
    try {
      // Create client for this settings configuration
      const client = await this.createClientForSettings(settings);
      
      if (!client || !client.supportsStreaming?.()) {
        throw new Error(`Provider ${settings.provider} does not support streaming`);
      }
      
      // Set up timeout
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Request timeout after ${this.config.timeout}ms`));
        }, this.config.timeout);
      });
      
      // Race between actual request and timeout
      await Promise.race([
        client.analyzeCorrectionsStreaming!(request, onProgress, onComplete),
        timeoutPromise
      ]);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Check if we should retry
      if (attempt < maxRetries && this.shouldRetry(error as Error)) {
        const delay = this.calculateRetryDelay(attempt);
        
        // Log retry attempt
        console.warn(`[StreamingAI] ${level} attempt ${attempt} failed, retrying in ${delay}ms: ${errorMessage}`);
        
        await this.sleep(delay);
        return this.attemptStreamingAnalysis(settings, level, request, onProgress, onComplete, attempt + 1);
      }
      
      // Max retries exceeded or non-retryable error
      throw error;
    }
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private calculateRetryDelay(attempt: number): number {
    if (!this.config.exponentialBackoff) {
      return this.config.retryDelay;
    }
    
    // Exponential backoff: 1s, 2s, 4s, 8s (max)
    const baseDelay = this.config.retryDelay;
    const exponentialDelay = baseDelay * Math.pow(2, attempt - 1);
    return Math.min(exponentialDelay, 8000); // Max 8 seconds
  }

  /**
   * Determine if error is retryable
   */
  private shouldRetry(error: Error): boolean {
    const message = error.message.toLowerCase();
    
    // Retryable errors
    if (message.includes('timeout') || 
        message.includes('network') || 
        message.includes('connection') ||
        message.includes('rate limit') ||
        message.includes('503') ||
        message.includes('502') ||
        message.includes('500')) {
      return true;
    }
    
    // Non-retryable errors
    if (message.includes('api key') ||
        message.includes('unauthorized') ||
        message.includes('forbidden') ||
        message.includes('400') ||
        message.includes('404')) {
      return false;
    }
    
    return true; // Default to retryable for unknown errors
  }

  /**
   * Record a fallback attempt for monitoring
   */
  private recordFallbackAttempt(
    level: 'primary' | 'secondary' | 'tertiary', 
    error: Error, 
    startTime: number
  ): void {
    const settings = level === 'primary' ? this.config.primary : 
                    level === 'secondary' ? this.config.secondary : 
                    this.config.tertiary;
    
    if (!settings) return;
    
    const attempt: FallbackAttempt = {
      provider: settings.provider,
      model: settings.selectedModel,
      attempt: this.fallbackHistory.filter(h => h.provider === settings.provider).length + 1,
      error: error.message,
      timestamp: startTime,
      responseTime: Date.now() - startTime
    };
    
    this.fallbackHistory.push(attempt);
  }

  /**
   * Get fallback attempt history
   */
  getFallbackHistory(): FallbackAttempt[] {
    return [...this.fallbackHistory];
  }

  /**
   * Clear fallback history
   */
  clearFallbackHistory(): void {
    this.fallbackHistory = [];
  }

  /**
   * Create a client instance for specific settings
   * This should be implemented by concrete classes or injected
   */
  protected abstract createClientForSettings(settings: any): Promise<AIClient>;

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}